import { useEffect, useRef } from 'react'
import { useAuthStore } from '@/features/auth/store/auth.store'
import { registerWebPush } from '@/sdk/instavcMessenger-instance'
import { isEmbedded } from '@/features/embed/embed-bridge'

/**
 * Registers this browser for FCM push once the user is authenticated. Mount once
 * in the protected area (alongside presence). Runs a single time per signed-in
 * user; the permission prompt shows on first mount, and the token is refreshed
 * on the SDK server each session (upsert on the server side).
 *
 * All push logic lives in the unified SDK (`@/sdk/instavcMessenger`); this hook is the
 * app-side trigger. Config binding is in `@/sdk/instavcMessenger-instance`.
 */
export function usePushRegistration(): void {
  const userId = useAuthStore((s) => s.user?.id)
  const doneFor = useRef<string | null>(null)

  useEffect(() => {
    if (!userId || doneFor.current === userId) return
    // A cross-origin frame cannot be granted notification permission, and push
    // for this user belongs to their own inMeet tab, not someone else's page.
    if (isEmbedded()) return
    doneFor.current = userId
    void registerWebPush(userId)
  }, [userId])
}
